import { Button, Input } from '@breetometer/components';
import { Beneficiaries, FiatInput, NoBeneficiary } from 'BreetComponents';
import {
	type CURRENCYCODETYPE,
	formatCurrencyInputAmount,
	getWithdrawRules,
	type IBeneficiary,
	NARRATION_MAX,
} from 'BreetHelpers';
import { useFiatWithdrawAmount, useFiatWithdrawRequestContext } from 'BreetHooks';
import { selectBeneficiaries, useAppSelector } from 'BreetRedux';
import { useCallback } from 'react';

const FormComponent = () => {
	const { userWalletProps, withdrawRequestHookForm, meta, formStage } = useFiatWithdrawRequestContext();

	const currencyCode = userWalletProps.code as CURRENCYCODETYPE;

	const beneficiaries = useAppSelector(selectBeneficiaries);

	const filteredBeneficiaries = beneficiaries.filter((i) => i.type !== 'virtual_account');

	const { withdrawalMaxAmount, withdrawalMinAmount } = getWithdrawRules(meta?.settings, currencyCode);

	const {
		watch,
		setValue,
		formState: { errors },
		register,
		handleSubmit,
	} = withdrawRequestHookForm;

	const { inputMessage, handleAmountChange, handleMaxClick } = useFiatWithdrawAmount({
		hookForm: withdrawRequestHookForm,
		userWalletProps,
		withdrawalMinAmount,
		withdrawalMaxAmount,
		currencyCode,
	});

	const selectBeneficiary = useCallback(
		(beneficiary: IBeneficiary | undefined) => {
			if (!beneficiary) return;

			setValue('selectedBeneficiary', {
				accountName: beneficiary.accountName,
				accountNumber: beneficiary.accountNumber,
				bankName: beneficiary.bankName,
				id: beneficiary._id,
			});

			setValue('narration', beneficiary.narration ?? '');
		},
		[setValue]
	);

	const isFormValid = !errors.amount?.message && !!watch('selectedBeneficiary.id') && !!watch('amount');

	return (
		<form
			onSubmit={handleSubmit(formStage.navigator.goTo('withdraw-details'))}
			data-testid='withdraw-form'
			noValidate
		>
			<FiatInput.WithdrawRequest
				label='Enter Amount'
				currencyCode={currencyCode}
				handleMaxClick={handleMaxClick}
				onChange={handleAmountChange}
				errorMessage={errors.amount?.message}
				message={inputMessage}
				value={formatCurrencyInputAmount(watch('amount'), currencyCode)}
			/>

			{!!filteredBeneficiaries.length && (
				<Beneficiaries
					onAddNewBeneficiary={formStage.navigator.goTo('add-beneficiary')}
					selectBeneficiary={selectBeneficiary}
					selectedBeneficiaryId={watch('selectedBeneficiary').id}
					title='Select Destination'
					onManageBeneficiaries={formStage.navigator.goTo('manage-beneficiaries')}
					filterOutType='virtual_account'
				/>
			)}

			<div className='new-drop-down-input'>
				<div className='narration-input'>
					<Input
						id='narration'
						label='Narration'
						className='bg-primary-500'
						widthType='full'
						placeholder=' '
						maxLength={NARRATION_MAX}
						{...register('narration')}
						errorMessage={errors.narration?.message}
						required
					/>
				</div>
			</div>

			{!filteredBeneficiaries.length && (
				<NoBeneficiary
					intent='initiate a withdrawal'
					header
					addAccount={formStage.navigator.goTo('add-beneficiary')}
				/>
			)}

			<div className='payment-request-form_cta new-btn-cta'>
				<Button
					label='Continue'
					type='submit'
					className='right-sheet_cta_fullwidth primary-hover'
					disabled={!isFormValid}
				/>
			</div>
		</form>
	);
};

export default FormComponent;
